<template>
  <div class="document-container">
    <es-loader
      v-if="loading"
      style="margin-top: 15rem"
    />
    <es-exception
      v-else-if="loadingError"
      margin="15rem"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <pid-retired-id-panel
        v-if="isRetired"
        :person="person"
      />
      <template v-if="$route.name === 'PidHistoryRecords' || $route.name === 'DmHistoryRecords'">
        <div
          class="document"
          :class="{ 'margin--top': isRetired }"
        >
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">
                  History Records
                </div>
              </template>
            </es-header>
          </div>
          <div class="content">
            <person-profile-history-table
              @updateSorter="updateSorter"
              :columnHeaders="columnHeaders"
              :historyRecords="historyRecords"
              :isRetired="isRetired"
              :person="person"
              :sortField="sortField"
              :sortOrder="sortOrder"
              :totalHistoryRecords="totalHistoryRecords"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <router-view name="personProfileSubBody"
          :isRetired="isRetired"
          :person="person"
        />
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import PersonProfileHistoryTable from '@/components/PersonProfileHistoryTable.vue';
import PidRetiredIdPanel from '@/components/PidRetiredIdPanel.vue';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  components: {
    EsException,
    EsHeader,
    EsLoader,
    PersonProfileHistoryTable,
    PidRetiredIdPanel,
  },
  mixins: [
    FieldLabels,
  ],
  props: {
    id: {
      type: String,
      required: true,
    },
    isRetired: {
      type: Boolean,
      default: false,
    },
    page: {
      type: String,
    },
    person: {
      type: Object,
      required: true,
    },
    sortField: {
      type: String,
      default: 'lastUpdateDate',
    },
    sortOrder: {
      type: String,
      default: 'DESC',
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      historyRecords: [],
      mutablePage: 1,
      mutableSortField: '',
      mutableSortOrder: '',
      loading: false,
      loadingError: false,
      totalHistoryRecords: 0,
    };
  },
  created() {
    this.mutableSortField = this.sortField;
    this.mutableSortOrder = this.sortOrder;
    this.onLoad();
    this.initializeColumnHeaders();
  },
  watch: {
    $route() {
      this.mutablePage = this.$route.query.page;
      this.onLoad();
    },
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
      'getFieldLabelByPersonType',
    ]),
    sortParams() {
      const params = {};
      params.sortField = this.mutableSortField;
      params.sortOrder = this.mutableSortOrder;
      return params;
    },
  },
  methods: {
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.person.personType);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.person.personType);
    },
    async getHistoryRecords() {
      const sort = [{
        field: this.mutableSortField,
        order: this.mutableSortOrder,
      }];
      const params = new URLSearchParams();
      params.append('page', (this.mutablePage) ? this.mutablePage : 1);
      params.append('sort', JSON.stringify(sort));
      let response = null;
      if (this.isRetired) {
        response = await axios.get(`retired-person/${this.id}/history`, { params });
      } else {
        response = await axios.get(`person/${this.id}/history`, { params });
      }
      this.historyRecords = response.data.data;
      this.totalHistoryRecords = response.data.totalCount;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    initializeColumnHeaders() {
      const ethnicityRaceHeader = [
        this.fieldEnabled('ETHNICITY_INDICATOR')
          ? this.fieldLabel('ETHNICITY_INDICATOR')
          : null,
        this.fieldEnabled('RACE_ETHNIC_CODE')
          ? this.fieldLabel('RACE_ETHNIC_CODE')
          : null,
      ]
        .filter(Boolean)
        .join(' / ');

      this.columnHeaders = [
        {
          field: 'lastUpdateDate',
          header: 'Update Date',
          show: true,
          sortable: true,
          style: {
            width: '10%',
          },
          title: 'Update Date',
        },
        {
          field: 'name',
          header: 'Name',
          show: this.fieldEnabled('FIRST_NAME') && this.fieldEnabled('LAST_NAME'),
          sortable: true,
          style: {
            width: '15%',
          },
          title: 'Name',
        },
        {
          field: 'dateOfBirth',
          header: this.fieldLabel('DATE_OF_BIRTH'),
          show: this.fieldEnabled('DATE_OF_BIRTH'),
          sortable: true,
          style: {
            width: '15%',
          },
          title: this.fieldLabel('DATE_OF_BIRTH'),
        },
        {
          field: 'genderCode',
          header: this.fieldLabel('GENDER_CODE'),
          show: this.fieldEnabled('GENDER_CODE'),
          sortable: false,
          style: {
            width: '5%',
          },
          title: this.fieldLabel('GENDER_CODE'),
        },
        {
          field: 'ethnicityRace',
          header: ethnicityRaceHeader,
          title: ethnicityRaceHeader,
          show:
            this.fieldEnabled('ETHNICITY_INDICATOR')
              || this.fieldEnabled('RACE_ETHNIC_CODE'),
          sortable: false,
          style: {
            width: '15%',
          },
        },
        {
          field: 'socialSecurityNumber',
          header: this.fieldLabel('SOCIAL_SECURITY_NUMBER'),
          show: this.fieldEnabled('SOCIAL_SECURITY_NUMBER'),
          sortable: false,
          style: {
            width: '10%',
          },
          title: this.fieldLabel('SOCIAL_SECURITY_NUMBER'),
        },
        {
          field: 'districtCode',
          header: 'Location Info',
          show: this.fieldEnabled('DISTRICT_CODE') && this.fieldEnabled('SCHOOL_CODE'),
          sortable: false,
          style: {
            width: '30%',
          },
          title: 'Location Info',
        },
        {
          field: null,
          header: 'Actions',
          show: true,
          sortable: false,
          style: {
            width: '5%',
          },
          title: 'Actions',
        },
      ];
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        await this.getHistoryRecords();
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      if (this.getSorter()) {
        this.mutableSortField = this.getSorter().field;
        this.mutableSortOrder = this.getSorter().order;
      }
      this.$router.replace({ query: this.sortParams });
    },
  },
};
</script>

<style lang="sass" scoped>
.margin--top
  margin-top: 1rem
</style>
